import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PostCard from '../components/PostCard';
import { AnalyticsWidget } from '../components/AnalyticsWidget';
import { TagWidget } from '../components/TagWidget';
import { getPosts } from '../../shared/api/posts';
import { getPublicTags } from '../../shared/api/tags';
import { getPublicCategories } from '../../shared/api/categories';
import { Post } from '../../shared/types/post';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getI18nStrings, type I18nStrings } from '../../shared/data/i18n';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { setStructuredData, createWebSiteStructuredData, removeStructuredData } from '../../shared/utils/structuredData';
import { getSiteUrl } from '../../shared/utils/metaTags';
import LanguageNoticeBanner from '../../shared/components/LanguageNoticeBanner';
import './Home.css';

function Home() {
  const lang = useLanguage();
  const i18n: I18nStrings = getI18nStrings(lang);
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [tagName, setTagName] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTag = searchParams.get('tag');
  const selectedCategory = searchParams.get('category');
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setTagName(null);
        setCategoryName(null);
        
        // タグとカテゴリーが指定されている場合と記事取得を並列実行
        // 英語の場合は最初から英語のタグ名/カテゴリー名を使用するため、データを取得してから画面描画を行う
        // カテゴリー名の表示のために、カテゴリーが指定されている場合のみカテゴリー一覧を取得
        const [tagsResponse, categoriesResponse, postsResponse] = await Promise.all([
          selectedTag ? getPublicTags().catch((error) => {
            // タグ取得エラーは無視（slugをそのまま使用）
            console.warn('Failed to fetch tags:', error);
            return null;
          }) : Promise.resolve(null),
          selectedCategory ? getPublicCategories().catch(() => {
            // カテゴリー取得エラーは無視（slugをそのまま使用）
            return null;
          }) : Promise.resolve(null),
          getPosts({ 
            status: 'published', 
            lang,
            tag: selectedTag || undefined,
            category: selectedCategory || undefined,
            page: currentPage,
            per_page: 10,
            skipAuth: true  // 公開エンドポイントなので認証トークンを送信しない
          })
        ]);
        
        // 公開済み（published）の記事を設定
        setPosts(postsResponse.posts);
        setTotal(postsResponse.meta.total);
        
        // タグ名を設定（タグが指定されている場合のみ）
        if (selectedTag && tagsResponse) {
          const tag = tagsResponse.tags.find(t => t.slug === selectedTag);
          if (tag) {
            // 言語に応じてタグ名を設定（英語の場合はnameEnを優先）
            const displayName = lang === 'en' && tag.nameEn ? tag.nameEn : tag.name;
            setTagName(displayName);
          } else {
            // タグが見つからない場合、記事が0件なら404ページにリダイレクト
            if (postsResponse.posts.length === 0 && postsResponse.meta.total === 0) {
              setLoading(false);
              navigate(`/${lang}/not-found-404`, { replace: true });
              return;
            }
            // タグが見つからない場合はslugをそのまま使用
            setTagName(selectedTag);
          }
        } else if (selectedTag) {
          // タグ取得に失敗した場合、記事が0件なら404ページにリダイレクト
          if (postsResponse.posts.length === 0 && postsResponse.meta.total === 0) {
            setLoading(false);
            navigate(`/${lang}/not-found-404`, { replace: true });
            return;
          }
          // タグ取得に失敗した場合はslugをそのまま使用
          setTagName(selectedTag);
        }
        
        // カテゴリー名を設定（カテゴリーが指定されている場合のみ）
        if (selectedCategory && categoriesResponse) {
          const category = categoriesResponse.categories.find(c => c.slug === selectedCategory);
          if (category) {
            // 言語に応じてカテゴリー名を設定（英語の場合はnameEnを優先）
            const displayName = lang === 'en' && category.nameEn ? category.nameEn : category.name;
            setCategoryName(displayName);
          } else {
            // カテゴリーが見つからない場合、記事が0件なら404ページにリダイレクト
            if (postsResponse.posts.length === 0 && postsResponse.meta.total === 0) {
              setLoading(false);
              navigate(`/${lang}/not-found-404`, { replace: true });
              return;
            }
            // カテゴリーが見つからない場合はslugをそのまま使用
            setCategoryName(selectedCategory);
          }
        } else if (selectedCategory) {
          // カテゴリー取得に失敗した場合、記事が0件なら404ページにリダイレクト
          if (postsResponse.posts.length === 0 && postsResponse.meta.total === 0) {
            setLoading(false);
            navigate(`/${lang}/not-found-404`, { replace: true });
            return;
          }
          // カテゴリー取得に失敗した場合はslugをそのまま使用
          setCategoryName(selectedCategory);
        }
        
        // total_pagesが返されない場合は、totalとper_pageから計算
        const calculatedTotalPages = postsResponse.meta.total_pages 
          || (postsResponse.meta.total !== undefined 
            ? Math.ceil(postsResponse.meta.total / postsResponse.meta.per_page)
            : 1);
        setTotalPages(calculatedTotalPages);
        
        // hasNextとhasPrevを計算
        // 優先順位: 1. totalから計算, 2. APIが返したhas_next/has_prev, 3. 取得した記事数から判断
        let calculatedHasNext: boolean;
        let calculatedHasPrev: boolean;
        
        if (postsResponse.meta.total !== undefined && postsResponse.meta.total > 0) {
          // totalが存在する場合は、それから正確に計算
          calculatedHasNext = currentPage * postsResponse.meta.per_page < postsResponse.meta.total;
          calculatedHasPrev = currentPage > 1;
        } else {
          // totalがない場合は、取得した記事数から判断
          // 取得した記事数がper_pageと同じ場合は、次のページが存在する可能性が高い
          calculatedHasNext = postsResponse.posts.length === postsResponse.meta.per_page;
          calculatedHasPrev = currentPage > 1;
          
          // APIがhas_next/has_prevを返している場合は、それも考慮
          // ただし、取得した記事数がper_pageと同じ場合は、hasNextをtrueにする
          if (postsResponse.meta.has_next !== undefined) {
            calculatedHasNext = postsResponse.meta.has_next || calculatedHasNext;
          }
          if (postsResponse.meta.has_prev !== undefined) {
            calculatedHasPrev = postsResponse.meta.has_prev || calculatedHasPrev;
          }
        }
        
        setHasNext(calculatedHasNext);
        setHasPrev(calculatedHasPrev);
      } catch (err) {
        setError(i18n.home.fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [lang, i18n, selectedCategory, selectedTag, currentPage]);

  // スマホ: タグ一覧でタグをクリックしたとき表示位置をリセットし、記事一覧が見えるようにする
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      window.scrollTo(0, 0);
    }
  }, [selectedTag]);

  // ページ変更ハンドラー
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (newPage === 1) {
      params.delete('page');
    } else {
      params.set('page', newPage.toString());
    }
    setSearchParams(params, { replace: true });
    // ページトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // メタタグと構造化データの設定
  const siteUrl = getSiteUrl();
  const homeUrl = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}`;
  
  // メタタグ用のデータを準備
  const metaTagsData = useMemo(() => {
    const title = selectedTag && tagName
      ? (lang === 'ja' ? `${tagName} - Version0.1` : `${tagName} - Version0.1`)
      : 'Version0.1';
    const description = selectedTag && tagName
      ? (lang === 'ja' ? `${tagName}に関する記事一覧` : `Articles about ${tagName}`)
      : (lang === 'ja' ? 'Version0.1 - やりたいところだけ実装するので永遠に完成しない。日々の作業ログや考えたことを残す技術ブログ。AWS中心の開発環境で、完成しないプロジェクトの記録と技術記事を公開' : 'Version0.1 - Will never be completed because I only implement what I want. A technical blog documenting daily work logs and thoughts. Sharing technical articles and project records, primarily using AWS');

    return {
      title,
      description,
      ogp: {
        title,
        description,
        url: homeUrl,
        type: 'website',
        siteName: 'Version0.1',
        image: '/og-image.png',
      },
      twitterCard: {
        card: 'summary' as const,
        title,
        description,
        image: '/og-image.png',
      },
      canonicalUrl: homeUrl,
    };
  }, [selectedTag, tagName, homeUrl, lang]);

  // メタタグを設定
  useMetaTags(metaTagsData);

  // WebSite構造化データを設定
  useEffect(() => {
    const webSiteData = createWebSiteStructuredData({
      name: 'Version0.1',
      url: homeUrl,
      description: lang === 'ja' ? 'Version0.1 - やりたいところだけ実装するので永遠に完成しない。日々の作業ログや考えたことを残す技術ブログ。AWS中心の開発環境で、完成しないプロジェクトの記録と技術記事を公開' : 'Version0.1 - Will never be completed because I only implement what I want. A technical blog documenting daily work logs and thoughts. Sharing technical articles and project records, primarily using AWS',
    });
    setStructuredData('website', webSiteData);

    // クリーンアップ
    return () => {
      removeStructuredData('website');
    };
  }, [homeUrl, lang]);


  return (
    <div className="home">
      {/* 言語案内バナー */}
      <LanguageNoticeBanner />
      <div className="home__container">
        <div className="home__layout">
          <main className="home__content">
            {loading ? (
              <LoadingSpinner text={i18n.home.loading} />
            ) : error ? (
              <p className="home__error">{error}</p>
            ) : posts.length === 0 ? (
              <p className="home__empty">
                {selectedTag && tagName
                  ? i18n.home.noArticlesWithTag(tagName)
                  : selectedCategory
                  ? (lang === 'ja' ? 'このカテゴリーに該当する記事がありません' : 'No articles found in this category')
                  : i18n.home.noArticles
                }
              </p>
            ) : (
              <>
                {(total !== undefined || selectedCategory) && (
                  <div className="home__meta">
                    <p className="home__count">
                      {selectedTag && tagName
                        ? i18n.home.articlesWithTag(tagName, total || 0)
                        : selectedCategory && categoryName
                        ? (lang === 'ja'
                            ? `${categoryName}カテゴリー: ${total || 0}件`
                            : `${categoryName} category: ${total || 0} articles`)
                        : i18n.home.totalArticles(total || 0)
                      }
                    </p>
                  </div>
                )}
                <div className="home__posts">
                  {posts.map((post) => (
                    <PostCard key={post.post_id} post={post} lang={lang} />
                  ))}
                </div>
                {(totalPages > 1 || hasNext || hasPrev) && (
                  <div className="home__pagination">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPrev}
                      className="home__pagination-button"
                    >
                      {(i18n.home as any).prevPage || '前へ'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNext}
                      className="home__pagination-button"
                    >
                      {(i18n.home as any).nextPage || '次へ'}
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
          <div className="home__sidebar">
            <div className="home__sidebar-inner">
              <AnalyticsWidget />
              <TagWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;